import { IconCheck, IconCross, rem } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { Fragment, MouseEvent, ReactNode, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Dot as RechartsDot,
  Line,
  LineChart,
  Rectangle,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  usePlotArea,
  YAxis,
} from 'recharts';
import { useHover } from 'usehooks-ts';

import Dot from '@/components/Dot/Dot';
import { useChartCursor } from '@/pages/Insights/Context/ChartCursorContext';
import { bucketEndOfInterval, useUrl } from '@/pages/Insights/Context/UrlContext';
import useSelection from '@/pages/Insights/hooks/useSelection';
import useXAxis from '@/pages/Insights/hooks/useXAxis';
import {
  ChartDataEntity,
  ChartDataPoint,
  ChartState,
  DataColorsType,
  DataPoint,
  TypeMetricUnit,
  TypeTimeInterval,
} from '@/pages/Insights/Insights.types';
import { metricTooltipDateText } from '@/pages/Insights/utils';
import {
  defaultCartesianGridStyle,
  defaultLineStyle,
  defaultTooltipStyle,
  defaultYaxisStyle,
} from '@/pages/Insights/utils/chartStyle';
import getFormattedValue from '@/pages/Insights/utils/getFormattedValue';
import getLineColor from '@/pages/Insights/utils/getLineColor';
import { intervalEndFromDate } from '@/pages/Insights/utils/time';

import chartStyle from './Chart.style';
import Messages from './Indicators/Messages';

export type ChartType = 'line' | 'stackedbar' | 'bar' | 'area';

const TOOLTIP_WIDTH = 300;
const Y_AXIS_WIDTH = 80;

const truncateString = (str: string) => {
  if (str.length < 30) {
    return str;
  }
  return `${str.slice(0, 27)}...`;
};

type LegendItemProps = {
  color: string;
  isNotHovered: boolean;
  isSelected: boolean;
  name: string;
  title?: string;
  onClick: (name: string, clickedElement: 'label' | 'box') => void;
  setHoveredLegend: (name: string) => void;
};

const LegendItem = (props: LegendItemProps) => {
  const { color, name, title = name, isNotHovered, isSelected, onClick, setHoveredLegend } = props;
  const SelectionIcon = isSelected ? IconCross : IconCheck;
  return (
    <Box
      as="li"
      cursor="pointer"
      display="flex"
      alignItems="center"
      gap="8"
      paddingY="4"
      paddingX="8"
      onMouseEnter={isSelected ? () => setHoveredLegend(name) : undefined}
      onMouseLeave={() => setHoveredLegend('')}
      opacity={isNotHovered ? '0.2' : '1'}
      role="group"
      title={title}
      transition="opacity 0.1s"
    >
      <Box
        backgroundColor={isSelected ? color : 'color/neutral/muted'}
        width="12"
        height="12"
        onClick={() => onClick(name, 'box')}
        borderRadius="2"
        _groupHover={{ boxShadow: 'outline' }}
      >
        <SelectionIcon
          width="12"
          height="12"
          opacity={0}
          _hover={{ opacity: 1 }}
          display="flex"
          color="icon/on-color"
        />
      </Box>
      <Text
        as="span"
        textStyle="body/md/regular"
        color={isSelected ? 'text/secondary' : 'text/tertiary'}
        onClick={() => onClick(name, 'label')}
      >
        {truncateString(title)}
      </Text>
    </Box>
  );
};

export interface ChartProps extends BoxProps {
  chartStart?: number;
  chartEnd?: number;
  data?: ChartDataEntity[];
  dataColors?: DataColorsType;
  dataUnit: TypeMetricUnit;
  emptyText?: string;
  error: AxiosError | null;
  height?: number;
  interval?: TypeTimeInterval;
  invertTreshold?: boolean;
  isError: boolean;
  isLoading: boolean;
  isMultiline?: boolean;
  refetch: () => void;
  showEmptyHelp?: boolean;
  showLegend?: boolean;
  syncId?: string;
  threshold?: number;
  verticalMarker?: number | { value: number; color: string };
  limitLabels?: Record<string, string>;
  legendLabels?: Record<string, string>;
  chartLimit?: { value: number; label: string };
  widgetId: string;
  withZoom?: boolean;
  type?: ChartType;
  yAxisTop?: number;
  forceTotal?: boolean;
  tooltipSummary?: (ctx: { totalValue: number }) => { label: string; value: ReactNode };
}

const linearScale = (opts: { domain: [number, number]; range: [number, number] }) => {
  const initialStart = opts.domain[0];
  const initialStop = opts.domain[1];
  const targetStart = opts.range[0];
  const targetStop = opts.range[1];

  return (value: number) => {
    return targetStart + (targetStop - targetStart) * ((value - initialStart) / (initialStop - initialStart));
  };
};

const xAxisDataKey = 'timestamp';
const indexValueOrEmptyString = (index?: number) => (index == null ? '' : index);
const conclusiveDataKeyForIndex = (index?: number): string => `conclusive_value_${indexValueOrEmptyString(index)}`;
const inconclusiveDataKeyForIndex = (index?: number): string => `inconclusive_value_${indexValueOrEmptyString(index)}`;
const trendlineDataKeyForIndex = (index?: number): string => `trendline_value_${indexValueOrEmptyString(index)}`;
const nameKeyForIndex = (index?: number): string => `name_${indexValueOrEmptyString(index)}`;
const timestampDataKey = `timestamp`;
const indexOfKey = (key: string): number => +(key.split('_').slice(-1)?.[0] || 0);

const getLargestYValue = (filteredData: ChartDataEntity[], yValueLineWhitelist: string[]): number => {
  const largest: { [key: number]: number } = {};
  filteredData.forEach((cde: ChartDataEntity) => {
    if (
      cde.name.toLowerCase() !== 'total' &&
      (yValueLineWhitelist.includes(cde.name) || yValueLineWhitelist.length === 0)
    ) {
      cde.dataPoints.forEach((dataPoint) => {
        const curr = largest?.[dataPoint.timestamp];
        if (curr === undefined) {
          largest[dataPoint.timestamp] = dataPoint.value;
        } else if (curr < dataPoint.value) {
          largest[dataPoint.timestamp] = dataPoint.value;
        }
      });
    }
  });
  return Math.max(...Object.values(largest));
};

const getLargestStackedYValue = (
  filteredData: ChartDataEntity[],
  yValueLineWhitelist: string[],
): { max: number; maxPerX: { [key: number]: number } } => {
  const largest: { [key: number]: number } = {};
  filteredData.forEach((cde: ChartDataEntity) => {
    if (
      cde.name.toLowerCase() !== 'total' &&
      (yValueLineWhitelist.includes(cde.name) || yValueLineWhitelist.length === 0)
    ) {
      cde.dataPoints.forEach((dataPoint) => {
        const curr = largest?.[dataPoint.timestamp];
        if (curr === undefined) {
          largest[dataPoint.timestamp] = dataPoint.value;
        } else {
          largest[dataPoint.timestamp] = curr + dataPoint.value;
        }
      });
    }
  });
  return { max: Math.max(...Object.values(largest)), maxPerX: largest };
};
const flattenedData = (
  data: ChartDataEntity[],
  chartInterval: TypeTimeInterval,
  xAxisDomain: [number, number],
  type: ChartType,
  yValueLineWhitelist: string[],
): {
  data: ChartDataPoint[];
  limits: Record<string, number>;
  lineNames: string[];
  lineDisplayNames: Record<string, string>;
  maxYValue: number;
  maxPerX: { [key: number]: number } | undefined;
} => {
  if (!data || data.length === 0) {
    return {
      data: [],
      limits: {},
      lineNames: [],
      lineDisplayNames: {},
      maxYValue: 0,
      maxPerX: undefined,
    };
  }
  const withinChartView = (payload: ChartDataPoint) =>
    payload[xAxisDataKey] >= xAxisDomain[0] && payload[xAxisDataKey] <= xAxisDomain[1];

  let filteredData: ChartDataEntity[];

  // remove total category when chart type is not stackedbar because it makes no sense to have a total value for bar charts and line
  filteredData = data.filter(
    (item: ChartDataEntity) => !(type === 'stackedbar' && item?.name?.toLowerCase() === 'total'),
  );

  // remove datapoints that are outside of chart view
  filteredData = filteredData.map((item: ChartDataEntity) => {
    return { ...item, dataPoints: item.dataPoints.filter(withinChartView) };
  });

  // remove categories that has no data points at all
  filteredData = filteredData.filter((item: ChartDataEntity) => item?.dataPoints?.length > 0);

  const remappedInconclusiveData: ChartDataPoint[] = [];
  const remappedConclusiveData: ChartDataPoint[] = [];
  const remappedTrendlineData: ChartDataPoint[] = [];

  const remappedNames: Record<string, string> = {};

  filteredData.forEach((item, index) => {
    remappedNames[nameKeyForIndex(index)] = item.name;
  });

  filteredData.forEach((item, index) => {
    const containsInconclusive = item.dataPoints.some((dataPoint) => {
      return bucketEndOfInterval(chartInterval, DateTime.fromMillis(dataPoint.timestamp)) > DateTime.utc();
    });

    let { dataPoints } = item;
    let inconclusiveDataPoints: DataPoint[] = [];

    if (containsInconclusive) {
      dataPoints = item.dataPoints.slice(0, -1);
      inconclusiveDataPoints = item.dataPoints.slice(type === 'line' ? -2 : -1);
    }

    dataPoints.forEach((val) => {
      remappedConclusiveData.push({
        [conclusiveDataKeyForIndex(index)]: val.value,
        [timestampDataKey]: val.timestamp,
      });
    });

    item.trendlineDataPoints?.forEach((val) => {
      remappedTrendlineData.push({
        [trendlineDataKeyForIndex(index)]: val.value,
        [timestampDataKey]: val.timestamp,
      });
    });

    inconclusiveDataPoints.forEach((val) => {
      remappedInconclusiveData.push({
        [inconclusiveDataKeyForIndex(index)]: val.value,
        [timestampDataKey]: val.timestamp,
      });
    });
  });

  const cc = remappedConclusiveData
    .concat(remappedInconclusiveData)
    .concat(remappedTrendlineData)
    .sort((a: ChartDataPoint, b: ChartDataPoint) => a.timestamp - b.timestamp);

  const dataByTimestamp: any = {};

  cc.forEach((item: ChartDataPoint) => {
    const { timestamp } = item;
    if (!dataByTimestamp[timestamp]) {
      dataByTimestamp[timestamp] = [];
    }
    dataByTimestamp[timestamp] = { ...dataByTimestamp[timestamp], ...item, ...remappedNames };
  });

  const dataMaxValue =
    type === 'line'
      ? getLargestYValue(filteredData, yValueLineWhitelist)
      : getLargestStackedYValue(filteredData, yValueLineWhitelist);

  return {
    data: Object.values(dataByTimestamp),
    limits: filteredData.reduce((accumulator: Record<string, number>, currentValue: ChartDataEntity) => {
      accumulator[currentValue.name] = currentValue.limit || 0;
      return accumulator;
    }, {}),
    lineNames: filteredData.map((e) => e.name),
    lineDisplayNames: filteredData.reduce(
      (acc, e) => {
        acc[e.name] = e.displayName || e.name;
        return acc;
      },
      {} as Record<string, string>,
    ),
    maxYValue: typeof dataMaxValue === 'number' ? dataMaxValue : dataMaxValue.max,
    maxPerX: typeof dataMaxValue !== 'number' ? dataMaxValue.maxPerX : undefined,
  };
};

const CustomCursor = ({ type, barWidthRef, x, y, width, height: cursorHeight, points }: any) => {
  if (!points) {
    const middleX = x + width / 2.0;
    // eslint-disable-next-line react-hooks/refs
    const wantedWidth = Math.min(type === 'stackedbar' ? barWidthRef.current : 2 * barWidthRef.current, width);
    return (
      <Rectangle
        x={middleX - wantedWidth / 2.0}
        y={y}
        fill="var(--colors-background\/active)"
        width={wantedWidth}
        height={cursorHeight}
      />
    );
  }

  return (
    <line
      x1={points[0].x}
      x2={points[1].x}
      y1={0}
      y2={cursorHeight}
      stroke="var(--colors-text\/body)"
      strokeWidth={1}
    />
  );
};

const Chart = (props: ChartProps) => {
  const {
    chartStart,
    chartEnd,
    data = [],
    dataColors,
    dataUnit,
    emptyText,
    error,
    height = 180,
    interval,
    isError,
    isLoading,
    limitLabels = {},
    isMultiline,
    invertTreshold,
    refetch,
    showEmptyHelp = true,
    showLegend = false,
    legendLabels = {},
    chartLimit,
    threshold,
    widgetId,
    withZoom = true,
    verticalMarker,
    type = 'line',
    yAxisTop = 100,
    forceTotal,
    tooltipSummary,
    ...rest
  } = props;

  const { chartEnd: chartEndFromUrl, chartStart: chartStartFromUrl, interval: intervalFromUrl } = useUrl();
  const chartInterval: TypeTimeInterval = interval || intervalFromUrl;
  const containerRef = useRef<HTMLDivElement>(null);
  const chartDataWindowRef = useRef<any>(null);
  const barWidthRef = useRef<number>(0);
  // SVG ids are document-global, so an index-only pattern id collides with every other chart on the
  // page and url(#…) resolves to whichever rendered first — showing another chart's colours.
  const instanceId = useId();

  const isChartHovered = useHover(containerRef);

  const chartWindowPadding = { left: 16, right: 16 };

  const { referenceLines, xAxis, xAxisDomain } = useXAxis({
    allowDuplicatedCategory: false,
    // Bars are centred on their timestamp, so the outermost ones overhang the axis range by half a
    // bar and allowDataOverflow clips them to it, cutting the first and last bar in half. Only
    // stackedbar opts out: its group is one barSize wide, so the overhang fits in the 16px padding.
    // Grouped bars are twice as wide and would spill into the y-axis gutter instead.
    allowDataOverflow: type !== 'stackedbar',
    chartEnd: chartEnd || chartEndFromUrl,
    chartStart: chartStart || chartStartFromUrl,
    dataKey: xAxisDataKey,
    padding: chartWindowPadding,
  });

  const {
    referenceArea: clickToZoomArea,
    onMouseDown,
    onMouseMove: selectionOnMouseMove,
    onMouseUp,
  } = useSelection({
    widgetId,
  });

  const [hoveredLegend, setHoveredLegend] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const chartData = useMemo(() => {
    return flattenedData(data, chartInterval, xAxisDomain, type, selectedItems);
  }, [chartInterval, data, type, xAxisDomain, selectedItems]);

  const colorMap: DataColorsType = dataColors || {};
  if (!isLoading && !dataColors && chartData.lineNames.length > 0) {
    chartData.lineNames
      .filter((name: string) => {
        if (type === 'line') return true;
        return name.toLowerCase() !== 'total';
      })
      .forEach((item: string, index: number) => {
        colorMap[item] = getLineColor(index + 1);
      });
  }
  const { cursorPosition: curPos, setCursorPosition } = useChartCursor();
  const yAxisDomain: [number, number] = [
    0,
    threshold !== undefined && threshold > chartData.maxYValue * 0.8
      ? threshold + chartData.maxYValue * 0.2
      : chartData.maxYValue,
  ];

  if (yAxisDomain[1] === 0 && yAxisTop > 0) {
    yAxisDomain[1] = yAxisTop;
  }

  if (chartLimit?.value && chartLimit.value > yAxisDomain[1]) {
    yAxisDomain[1] = chartLimit.value;
  }

  const yAxisWidth =
    // eslint-disable-next-line react-hooks/refs
    (chartDataWindowRef?.current?.props?.chartWidth || 0) - (chartDataWindowRef?.current?.props?.width || 0);

  //
  // Chart components

  const yAxisTopPadding = 16;

  const yAxis = (
    <YAxis
      {...defaultYaxisStyle(dataUnit)}
      domain={yAxisDomain}
      axisLine={{ stroke: 'var(--colors-border\\/hover)' }}
      tickLine={{ stroke: 'var(--colors-border\\/hover)' }}
      padding={{ top: yAxisTopPadding }}
    />
  );
  const ghostedCursor = (() => {
    if (!curPos || chartData.lineNames.length === 0 || isChartHovered) {
      return null;
    }

    // eslint-disable-next-line react-hooks/refs
    const barWidth = Math.min(type === 'stackedbar' ? barWidthRef.current : 2 * barWidthRef.current);
    const x = type === 'line' || type === 'area' ? curPos : curPos - barWidth / 2;

    if (x >= yAxisWidth) {
      if (type === 'line' || type === 'area') {
        return <line x1={x} x2={x} y1={0} y2={height - 30} stroke="var(--colors-text\/body)" strokeWidth={1} />;
      }
      return (
        <rect
          x={x}
          width={barWidth}
          y={0}
          height={height - 30}
          fill="var(--colors-background\/active)"
          opacity={0.75}
        />
      );
    }
    return null;
  })();

  const cartesianCustomBg =
    !isLoading && threshold !== undefined && !Number.isNaN(threshold) && chartData.lineNames?.length > 0;
  const thresholdLineAndArea = !isLoading && threshold !== undefined && !Number.isNaN(threshold) && (
    <>
      {invertTreshold ? (
        // recharts v3 removed `isFront` (a no-op since pre-v2); paint order now follows JSX order.
        <ReferenceArea y1={threshold} y2={0} fill="var(--colors-status\/critical\/bg)" ifOverflow="visible" />
      ) : (
        <ReferenceArea
          y1={threshold}
          fill="var(--colors-status\/critical\/bg)"
          ifOverflow="visible"
          shape={(shapeProps) =>
            shapeProps.y >= 0 ? (
              <Rectangle {...shapeProps} height={shapeProps.height + shapeProps.y} y={0} />
            ) : (
              <>{null}</>
            )
          }
        />
      )}
      <ReferenceLine y={threshold} stroke="var(--colors-color\/red\/strong)" strokeWidth={1} />
    </>
  );

  const hoveredLine = useRef<[string, unknown]>();
  const dot = ({ key: _key, ...e }: any) => {
    const isPositionOfDotUnavailable = e.value == null || e.cx == null || e.cy == null;
    if (isPositionOfDotUnavailable) return <>null</>;

    const isDataPointFromInconclusiveSource = e.dataKey.startsWith(inconclusiveDataKeyForIndex());
    const isDataPointInconclusive = isDataPointFromInconclusiveSource;
    let isThisTheNearestDotToMouseCursor = false;
    let isLineContainsASinglePoint = false;

    if (isChartHovered) {
      const { height: chartWindowHeight } = e;

      const paddingScaler = linearScale({
        range: [yAxisDomain[0], yAxisDomain[1]],
        domain: [0, chartWindowHeight],
      });

      // transform mouse pointer y pixel values to y axis values
      const scaler = linearScale({
        domain: [chartWindowHeight, 0],
        range: [yAxisDomain[0], yAxisDomain[1] + paddingScaler(yAxisTopPadding)],
      });
      const valueAtMouseY = scaler(mouseCursorPosition.y);

      // find out if on the current x value which do is the nearest to the mouse cursor y value
      const valuesFromPayload = Object.entries(e.payload).filter(
        (keyAndValue: [string, any]) =>
          keyAndValue[0].startsWith(conclusiveDataKeyForIndex()) ||
          keyAndValue[0].startsWith(inconclusiveDataKeyForIndex()),
      );
      const closestYValueToMouseYPosition = valuesFromPayload.reduce((prev, curr) => {
        return Math.abs((curr[1] as number) - valueAtMouseY) < Math.abs((prev[1] as number) - valueAtMouseY)
          ? curr
          : prev;
      });

      isThisTheNearestDotToMouseCursor =
        closestYValueToMouseYPosition[0] === e.dataKey &&
        closestYValueToMouseYPosition[1] === e.value &&
        lastLabel.current === e.payload.timestamp;

      if (isThisTheNearestDotToMouseCursor) hoveredLine.current = closestYValueToMouseYPosition;

      const currentlyHoveredLineName = e.payload[nameKeyForIndex(indexOfKey(closestYValueToMouseYPosition[0]))];
      if (isThisTheNearestDotToMouseCursor && hoveredLegend !== currentlyHoveredLineName) {
        setHoveredLegend(currentlyHoveredLineName);
      }
    } else {
      const numberOfDataPointsForSameLine = chartData.data.filter((dp: ChartDataPoint) => dp[e.dataKey]).length;
      if (numberOfDataPointsForSameLine === 1) {
        isLineContainsASinglePoint = true;
      }
    }
    return (
      <RechartsDot
        {...e}
        r={isThisTheNearestDotToMouseCursor ? 4 : isLineContainsASinglePoint ? 3 : 2}
        strokeWidth={!isDataPointInconclusive ? 2 : 1}
        strokeDasharray={undefined}
        stroke={!isDataPointInconclusive ? undefined : e.stroke}
        fill={!isDataPointInconclusive ? e.stroke : 'var(--colors-background\\/primary)'}
      />
    );
  };

  const linesAndAreas =
    (type === 'line' || type === 'area') &&
    !isLoading &&
    chartData.lineNames.map((name: string, index: number) => {
      if (selectedItems.length > 0 && !selectedItems?.includes(name)) return null;

      const containsInconclusive = !!chartData.data.find((d: ChartDataPoint) => d[inconclusiveDataKeyForIndex(index)]);
      const containsTrendline = !!chartData.data.find((d: ChartDataPoint) => d[trendlineDataKeyForIndex(index)]);
      const lineColor = isMultiline ? colorMap[name].css : 'var(--colors-color\\/turquoise\\/bold)';
      const isNotHighlighted = hoveredLegend !== '' && hoveredLegend !== name;

      if (type === 'area') {
        return (
          <Fragment key={name}>
            {containsTrendline && (
              <Area
                isAnimationActive={false}
                connectNulls
                stackId="stack"
                fill="var(--colors-color\/neutral\/base)"
                fillOpacity={0.1}
                stroke="var(--colors-color\/neutral\/base)"
                dot={false}
                activeDot={false}
                dataKey={trendlineDataKeyForIndex(index)}
                onMouseEnter={() => setHoveredLegend(name)}
              />
            )}

            {containsInconclusive && (
              <Area
                {...defaultLineStyle}
                isAnimationActive={false}
                dot={dot}
                activeDot={false}
                stroke={lineColor}
                fill={lineColor}
                stackId="stack"
                fillOpacity={isNotHighlighted ? 0.05 : 0.2}
                strokeOpacity={isNotHighlighted ? 0.2 : 1}
                strokeWidth={isNotHighlighted ? 1 : 2}
                strokeDasharray="3 3"
                dataKey={inconclusiveDataKeyForIndex(index)}
                onMouseEnter={() => setHoveredLegend(name)}
              />
            )}

            <Area
              {...defaultLineStyle}
              isAnimationActive={false}
              dot={dot}
              activeDot={false}
              stackId="stack"
              dataKey={conclusiveDataKeyForIndex(index)}
              stroke={lineColor}
              strokeOpacity={isNotHighlighted ? 0.2 : 1}
              strokeWidth={isNotHighlighted ? 1 : 2}
              fill={lineColor}
              fillOpacity={isNotHighlighted ? 0.05 : 0.2}
              onMouseEnter={() => setHoveredLegend(name)}
            />
          </Fragment>
        );
      }

      return (
        <Fragment key={name}>
          {containsTrendline && (
            <Line
              isAnimationActive={false}
              connectNulls
              strokeWidth={4}
              stroke="var(--colors-color\/neutral\/base)"
              dot={false}
              activeDot={false}
              dataKey={trendlineDataKeyForIndex(index)}
              strokeOpacity={0.1}
            />
          )}

          {containsInconclusive && (
            <Line
              {...defaultLineStyle}
              isAnimationActive={false}
              dot={dot}
              activeDot={false}
              stroke={lineColor}
              strokeOpacity={isNotHighlighted ? 0.2 : 1}
              strokeDasharray="3 3"
              dataKey={inconclusiveDataKeyForIndex(index)}
            />
          )}

          <Line
            {...defaultLineStyle}
            isAnimationActive={false}
            dot={dot}
            activeDot={false}
            dataKey={conclusiveDataKeyForIndex(index)}
            stroke={lineColor}
            strokeOpacity={isNotHighlighted ? 0.2 : 1}
          />
        </Fragment>
      );
    });

  const getBars = () => {
    if (isLoading) {
      return null;
    }

    return chartData.lineNames
      .filter((v: string) => v.toLowerCase() !== 'total')
      .map((lineName: string, index: number) => {
        const containsInconclusive = !!chartData.data.find(
          (d: ChartDataPoint) => d[inconclusiveDataKeyForIndex(index)],
        );
        const lineColor = colorMap[lineName].css;
        const isNotHighlighted = hoveredLegend !== '' && hoveredLegend !== lineName;

        return (
          <Fragment key={lineName}>
            {containsInconclusive && (
              <>
                <pattern
                  id={`pattern-stripe-${instanceId}-${index}`}
                  patternUnits="userSpaceOnUse"
                  width="5"
                  height="5"
                  patternTransform="rotate(45)"
                >
                  <rect x="0" y="0" width="100%" height="100%" fill="var(--colors-background\/primary)" />
                  <line x1="0" y="0" x2="0" y2="5" stroke={lineColor} strokeWidth="8" />
                </pattern>
                <Bar
                  stackId={type === 'stackedbar' ? 'stack' : lineName}
                  hide={selectedItems.length > 0 && !selectedItems?.includes(lineName)}
                  isAnimationActive={false}
                  fill={`url(#pattern-stripe-${instanceId}-${index})`}
                  fillOpacity={isNotHighlighted ? 0.2 : 1}
                  dataKey={inconclusiveDataKeyForIndex(index)}
                  onMouseEnter={type === 'stackedbar' ? () => setHoveredLegend(lineName) : undefined}
                />
              </>
            )}
            <Bar
              stackId={type === 'stackedbar' ? 'stack' : lineName}
              hide={selectedItems.length > 0 && !selectedItems?.includes(lineName)}
              isAnimationActive={false}
              dataKey={conclusiveDataKeyForIndex(index)}
              fill={lineColor}
              fillOpacity={isNotHighlighted ? 0.2 : 1}
              onMouseEnter={type === 'stackedbar' ? () => setHoveredLegend(lineName) : undefined}
              background={(e: any) => {
                barWidthRef.current = e.width;
                return <> </>;
              }}
            />
          </Fragment>
        );
      });
  };

  const verticalMarkerLineAndArea = !isLoading && verticalMarker && (
    <ReferenceLine
      ifOverflow="extendDomain"
      x={typeof verticalMarker === 'number' ? verticalMarker : verticalMarker.value}
      stroke={typeof verticalMarker === 'number' ? 'var(--colors-text\\/body)' : verticalMarker.color}
      strokeWidth={1}
    />
  );

  // recharts v3: Tooltip is no longer generic; its `content` render prop receives TooltipContentProps.
  const customTooltip = (contentProps: TooltipContentProps) => {
    const { active, payload, label: xLabel } = contentProps;

    const label = payload?.[0]?.payload[xAxisDataKey] || xLabel;
    const shouldHideTooltip = !active || !label || !curPos;

    if (shouldHideTooltip) {
      return <>{null}</>;
    }

    const lineName = hoveredLine.current
      ? chartData.lineNames[indexOfKey(hoveredLine.current[0])]
      : chartData.lineNames[0];
    const lineDisplayName = chartData.lineDisplayNames[lineName];
    const value = hoveredLine.current ? (hoveredLine.current[1] as number) : 0;
    const inconclusive = hoveredLine.current?.[0].startsWith(inconclusiveDataKeyForIndex());
    const dateFrom = new Date(label);
    const dateTo = intervalEndFromDate(dateFrom, chartInterval);
    const { primaryDateText } = metricTooltipDateText[chartInterval](dateFrom, dateTo);
    const lineColor = (isMultiline && colorMap[lineName]?.name) || 'color/turquoise/bold';

    return (
      <Box
        backgroundColor="background/fixed-contrast"
        maxWidth={rem(TOOLTIP_WIDTH)}
        paddingLeft="8"
        paddingRight="8"
        paddingTop="6"
        paddingBottom="6"
        borderRadius="6"
      >
        <Box display="flex" justifyContent="flex-start" marginBottom="4" alignItems="center" gap={4}>
          <Dot
            size="8"
            backgroundColor={inconclusive ? 'color/white/100' : lineColor}
            border={inconclusive ? '2px solid' : undefined}
            borderColor={lineColor}
            flexShrink={0}
          />
          <Text as="span" textStyle="heading/h6" wordBreak="break-all" color="text/on-color">
            {lineDisplayName}
          </Text>
          <Text
            as="span"
            textStyle="body/sm/regular"
            textTransform="uppercase"
            color="text/on-color"
            marginLeft="auto"
            whiteSpace="nowrap"
          >
            {getFormattedValue(dataUnit, value)}
          </Text>
        </Box>
        <Text textStyle="body/sm/regular" textTransform="uppercase" color="color/white/80">
          {primaryDateText}
        </Text>
      </Box>
    );
  };

  const [barYValue, setBarYValue] = useState(0);
  const [barXValue, setBarXValue] = useState(0);

  // recharts v3 no longer passes `viewBox`/`position` to tooltip content. Read the plot
  // rectangle from the `usePlotArea` hook (with a defensive chart-ref/height fallback), and
  // take the active x from the `coordinate` content prop: the manual `curPos` cursor tracking
  // stays null on bar charts in v3, which is what made the tooltip stop showing.
  // (`coordinate.x` is the same active-coordinate x that used to feed `curPos`.)
  const CustomBarTooltip = (contentProps: TooltipContentProps) => {
    const plotArea = usePlotArea();
    const { active, payload, label: xLabel, coordinate } = contentProps;

    const label = payload?.[0]?.payload[xAxisDataKey] || xLabel;

    // `chartDataWindowRef.current.props.width` is the recharts-computed plot width (what the
    // old `viewBox.width` gave); height falls back to the container height minus the ~30px
    // x-axis band (the same assumption the cursor uses).
    const chartWindowWidth = plotArea?.width ?? chartDataWindowRef.current?.props?.width ?? 0;
    const chartWindowHeight = plotArea?.height ?? Math.max(0, height - 30);
    const paddingScaler = linearScale({
      range: [yAxisDomain[0], yAxisDomain[1]],
      domain: [0, chartWindowHeight],
    });
    const scaler = linearScale({
      domain: [yAxisDomain[0], yAxisDomain[1] + paddingScaler(yAxisTopPadding)],
      range: [chartWindowHeight, 0],
    });

    // These feed the outer `<Tooltip position>`. recharts renders this content as its own
    // component, so writing them during render triggers a "cannot update a component while
    // rendering a different component" warning — defer to a layout effect (runs before paint,
    // so the tooltip doesn't visibly jump).
    const nextBarX = active && label && coordinate ? coordinate.x : null;
    const nextBarY = active && label && coordinate ? scaler(chartData.maxPerX?.[label] || 0) : null;
    useLayoutEffect(() => {
      if (nextBarX != null) setBarXValue(nextBarX);
      if (nextBarY != null) setBarYValue(nextBarY);
    }, [nextBarX, nextBarY]);

    if (!active || !label || !coordinate) {
      return <>{null}</>;
    }

    const dotdata: {
      limit?: number;
      name: string;
      displayName: string;
      value: number;
      key: string;
    }[] = [];
    const filtered = chartData.data.filter((item: ChartDataPoint) => item.timestamp === label);
    filtered.forEach((item: ChartDataPoint) => {
      Object.keys(item).forEach((key) => {
        if (key.startsWith(inconclusiveDataKeyForIndex()) || key.startsWith(conclusiveDataKeyForIndex())) {
          const index = indexOfKey(key);
          const name = item[nameKeyForIndex(index)] as string;
          const limit = chartData.limits[name];
          const displayName = chartData.lineDisplayNames[name];
          const value = item[key] as number;
          dotdata.push({ limit, name, value, key, displayName });
        }
      });
    });

    const dateFrom = new Date(label);
    const dateTo = intervalEndFromDate(dateFrom, chartInterval);
    const { primaryDateText } = metricTooltipDateText[chartInterval](dateFrom, dateTo);

    let totalItemValue = data
      .find((d: ChartDataEntity) => d.name.toLowerCase() === 'total')
      ?.dataPoints.find((d: DataPoint) => d.timestamp === label)?.value;
    const showTotal = Boolean(totalItemValue || forceTotal);
    if (!totalItemValue && (forceTotal || tooltipSummary)) {
      totalItemValue = dotdata.reduce((total, item) => total + item.value, 0);
    }

    let translateX = '50%';
    const translateXValue = TOOLTIP_WIDTH - (chartWindowWidth - (coordinate.x - Y_AXIS_WIDTH) + 20);
    if (translateXValue > 0) {
      translateX = `${translateXValue}px`;
    }

    let tooltipSummaryProps: ReturnType<NonNullable<typeof tooltipSummary>> | null = null;
    if (tooltipSummary && totalItemValue) {
      tooltipSummaryProps = tooltipSummary({ totalValue: totalItemValue });
    }

    return (
      <Box
        backgroundColor="background/fixed-contrast"
        width={rem(TOOLTIP_WIDTH)}
        paddingLeft="8"
        paddingRight="8"
        paddingTop="6"
        paddingBottom="6"
        borderRadius="6"
        transform={`translate(-${translateX},-100%) translateY(-10px)`}
      >
        <Box
          width="8"
          height="8"
          backgroundColor="background/fixed-contrast"
          position="absolute"
          transform="translateX(-4px) rotate(45deg)"
          bottom="-4"
          left={translateX}
        />

        {showTotal && (
          <Box display="flex" justifyContent="space-between" marginBottom="4" alignItems="center" gap={4}>
            <Text textStyle="sans/size-2/bold" color="text/on-color">
              Total
            </Text>
            <Text textStyle="sans/size-2/bold" color="color/white/90">
              {getFormattedValue(dataUnit, totalItemValue)}
            </Text>
          </Box>
        )}

        {dotdata.map((entry) => {
          const { limit, name, displayName, value, key: dataKey } = entry;

          const lineName = name;

          const inconclusive = dataKey.startsWith(inconclusiveDataKeyForIndex());
          const lineColor = isMultiline ? colorMap[lineName].name : 'color/turquoise/bold';
          const isHighlighted = hoveredLegend === name;

          let lineLabel: ReactNode = getFormattedValue(dataUnit, value);
          if (limit) {
            lineLabel = (
              <>
                <strong>{getFormattedValue(dataUnit, value)}</strong>/{getFormattedValue(dataUnit, limit)}
              </>
            );
          }

          return (
            <Box
              key={dataKey}
              display="flex"
              justifyContent="flex-start"
              marginBottom="4"
              alignItems="center"
              gap="8"
              backgroundColor={isHighlighted ? 'color/white/10' : undefined}
              marginX={isHighlighted ? '-8' : undefined}
              paddingX={isHighlighted ? '8' : undefined}
            >
              <Box
                width="12"
                height="12"
                borderRadius="2"
                backgroundColor={inconclusive ? 'color/white/100' : lineColor}
                border="2px solid"
                borderColor={lineColor}
                flexShrink={0}
              />
              <Text
                as="span"
                textStyle="body/md/regular"
                wordBreak="break-all"
                color={isHighlighted ? 'text/on-color' : 'color/white/80'}
              >
                {displayName}
              </Text>
              <Text
                as="span"
                textStyle="body/md/regular"
                color={isHighlighted ? 'text/on-color' : 'color/white/80'}
                marginLeft="auto"
                whiteSpace="nowrap"
              >
                {lineLabel}
              </Text>
            </Box>
          );
        })}
        {tooltipSummaryProps && (
          <Box display="flex" justifyContent="flex-start" marginBottom="4" alignItems="center" gap="8">
            <Box width="12" height="12" flexShrink={0} />
            <Text as="span" textStyle="body/md/regular" wordBreak="break-all" color="color/white/80">
              {tooltipSummaryProps.label}
            </Text>
            <Text as="span" textStyle="body/md/regular" color="color/white/80" marginLeft="auto" whiteSpace="nowrap">
              {tooltipSummaryProps.value}
            </Text>
          </Box>
        )}

        <Text textStyle="body/md/regular" color="color/white/80">
          {primaryDateText}
        </Text>
      </Box>
    );
  };

  const tooltip = (() => {
    if (chartData.lineNames.length > 0 && !isLoading) {
      return (
        <Tooltip
          {...defaultTooltipStyle}
          content={type === 'line' ? customTooltip : CustomBarTooltip}
          animationDuration={0}
          offset={12}
          cursor={<CustomCursor type={type} barWidthRef={barWidthRef} />}
          wrapperStyle={{ visibility: 'visible', zIndex: 10 }}
          position={type === 'line' ? undefined : { x: barXValue, y: barYValue }}
        />
      );
    }
    return null;
  })();

  const getLimitLines = () => {
    if (isLoading) {
      return null;
    }

    const lines = chartData.lineNames.map((lineName: string) => {
      const limit = chartData.limits[lineName];
      if (!limit || (selectedItems.length !== 0 && !selectedItems.includes(lineName))) {
        return;
      }
      return (
        <ReferenceLine
          key={`limit-line-${lineName}`}
          y={limit}
          yAxisId={0}
          stroke={colorMap[lineName].css}
          strokeDasharray="3 3"
          strokeWidth={1}
          label={{
            value: limitLabels[lineName],
            position: 'insideBottomLeft',
            style: { fontSize: 10 },
            fill: 'var(--colors-text\\/body)',
          }}
        />
      );
    });
    if (chartLimit?.value) {
      lines.push(
        <ReferenceLine
          key={`limit-line-chartlimit`}
          y={chartLimit.value}
          yAxisId={0}
          stroke="var(--colors-purple-50)"
          strokeDasharray="3 3"
          strokeWidth={1}
          label={{
            value: chartLimit.label,
            position: 'insideBottomLeft',
            style: { fontSize: 10 },
            fill: 'var(--colors-text\\/body)',
          }}
        />,
      );
    }
    return lines;
  };

  //
  // Chart events

  const onCursorMoveInsideChartArea = (chartState: ChartState, e: MouseEvent<SVGGraphicsElement>) => {
    if (isChartHovered && chartState.activeCoordinate) {
      if (curPos !== Math.round(chartState.activeCoordinate.x)) {
        const x =
          type === 'line'
            ? Math.round(chartState.activeCoordinate.x)
            : linearScale({
                domain: [yAxisWidth, chartDataWindowRef?.current?.props?.chartWidth || 0],
                range: [yAxisWidth, chartDataWindowRef?.current?.props?.chartWidth || 0],
              })(Math.round(chartState.activeCoordinate.x));

        setCursorPosition(x);
      }
      if (withZoom) {
        const compatibleChartState = {
          ...chartState,
          activeLabel: chartState.activeLabel !== undefined ? String(chartState.activeLabel) : undefined,
        };
        selectionOnMouseMove(compatibleChartState, e);
      }
    }
  };

  const [mouseCursorPosition, setMouseCursorPosition] = useState({ x: 0, y: 0 });
  const lastLabel = useRef(0);

  // Which stacked segment (line) is the cursor's y over, in the column at `activeLabel`?
  // Driven from the pointer position rather than per-<Bar>/<Area> `onMouseEnter`, which is
  // unreliable in recharts v3 (it races with the fillOpacity re-render and the activeLabel
  // reset below). Returns '' when the cursor is above the stack or off a column.
  const getHoveredStackName = (activeLabel: string | number | undefined, y: number): string => {
    // Iterate `lineNames` in render order with its own index: the conclusive/inconclusive data
    // keys are built from the same index, and 'total' is only stripped for stackedbar (so it's
    // absent there and present-but-matching for areas) — matching both the bar and area stacks.
    const names = chartData.lineNames;
    const row = chartData.data.find((d: ChartDataPoint) => String(d.timestamp) === String(activeLabel));
    if (!row || names.length === 0) {
      return '';
    }

    const plotHeight = chartDataWindowRef.current?.props?.height ?? Math.max(0, height - 30);
    const paddingScaler = linearScale({ range: [yAxisDomain[0], yAxisDomain[1]], domain: [0, plotHeight] });
    const valueAtY = linearScale({
      domain: [plotHeight, 0],
      range: [yAxisDomain[0], yAxisDomain[1] + paddingScaler(yAxisTopPadding)],
    })(y);

    let cumulative = 0;
    for (let i = 0; i < names.length; i += 1) {
      const segmentValue =
        (Number(row[conclusiveDataKeyForIndex(i)]) || 0) + (Number(row[inconclusiveDataKeyForIndex(i)]) || 0);
      if (valueAtY >= cumulative && valueAtY < cumulative + segmentValue) {
        return names[i];
      }
      cumulative += segmentValue;
    }
    return '';
  };

  const onMouseMoveInsideChartArea = (chartState: ChartState, e: MouseEvent<SVGGraphicsElement>) => {
    if (isChartHovered) {
      // recharts v3's handler state no longer carries chartX/chartY (raw pointer position).
      // Derive it from the pointer position relative to the chart surface (`currentTarget`);
      // `offsetX/Y` would be relative to the hovered child element, which jumps around.
      const rect = e?.currentTarget?.getBoundingClientRect();
      if (!chartState || !rect) {
        setMouseCursorPosition({ x: 0, y: 0 });
        return;
      }
      const cpos = { x: Math.round(e.clientX - rect.left), y: Math.round(e.clientY - rect.top) };
      const newLocation = cpos.x !== mouseCursorPosition.x || cpos.y !== mouseCursorPosition.y;
      if (newLocation) {
        setMouseCursorPosition(cpos);
      }

      if (!chartState.activeLabel) {
        lastLabel.current = 0;
        onMouseLeaveChartArea();
      } else {
        if (chartState.activeLabel !== lastLabel.current) {
          onCursorMoveInsideChartArea(chartState, e);
          lastLabel.current = chartState.activeLabel as number;
        }
        if (type === 'stackedbar' || type === 'area') {
          setHoveredLegend(getHoveredStackName(chartState.activeLabel, cpos.y));
        }
      }
    }
  };

  const onMouseLeaveChartArea = () => {
    setCursorPosition(null);
    lastLabel.current = 0;
    setHoveredLegend('');
  };

  const onMouseDownInsideChartArea = (chartState: ChartState, e: MouseEvent<SVGGraphicsElement>) => {
    if (withZoom && isChartHovered) {
      const compatibleChartState = {
        ...chartState,
        activeLabel: chartState.activeLabel !== undefined ? String(chartState.activeLabel) : undefined,
      };
      onMouseDown(compatibleChartState, e);
    }
  };

  const onMouseUpInsideChartArea = (chartState: ChartState, e: MouseEvent<SVGGraphicsElement>) => {
    if (withZoom && isChartHovered) {
      const compatibleChartState = {
        ...chartState,
        activeLabel: chartState.activeLabel !== undefined ? String(chartState.activeLabel) : undefined,
      };
      onMouseUp(compatibleChartState, e);
    }
  };

  const onLegendItemClick = (name: string, clickedElement: 'label' | 'box') => {
    if (clickedElement === 'label') {
      if (!selectedItems?.includes(name)) {
        setSelectedItems([name]);
      } else if (selectedItems?.length > 1) {
        setSelectedItems([name]);
      } else {
        setSelectedItems([]);
      }
    }

    if (clickedElement === 'box') {
      if (selectedItems?.length === 0) {
        setSelectedItems(chartData.lineNames.filter((v) => v !== name));
      } else if (selectedItems?.includes(name)) {
        setSelectedItems(selectedItems.filter((v) => v !== name));
      } else {
        setSelectedItems([name, ...selectedItems]);
      }
    }
  };

  //
  // Chart body

  const ChartComponent = (() => {
    if (type === 'line') {
      return LineChart;
    }
    if (type === 'area') {
      return AreaChart;
    }
    return BarChart;
  })();

  const getChartContent = () => {
    if (type === 'line' || type === 'area') {
      return linesAndAreas;
    }
    return getBars();
  };

  return (
    <Box ref={containerRef} {...rest} css={chartStyle(withZoom)}>
      <ResponsiveContainer height={height}>
        <ChartComponent
          margin={{
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          data={chartData.data}
          throttleDelay={0}
          // recharts v3 defaults this to true, which makes the chart surface focusable and
          // shows a browser focus ring on click (not present pre-v3). We drive interaction
          // via mouse handlers, so disable the keyboard a11y layer to restore the old look.
          accessibilityLayer={false}
          onMouseMove={onMouseMoveInsideChartArea}
          onMouseDown={onMouseDownInsideChartArea}
          onMouseUp={onMouseUpInsideChartArea}
          onMouseLeave={onMouseLeaveChartArea}
          barSize={32}
          ref={chartDataWindowRef}
        >
          <CartesianGrid {...defaultCartesianGridStyle(height, 0, cartesianCustomBg)} />
          <Messages
            error={error}
            isFetching={isLoading}
            isFailing={isError}
            onRetry={refetch}
            isEmpty={chartData.lineNames?.length === 0}
            emptyText={emptyText}
            showEmptyHelp={showEmptyHelp}
          />
          {withZoom && clickToZoomArea}
          {referenceLines}
          {xAxis}
          {yAxis}
          {tooltip}
          {ghostedCursor}
          {thresholdLineAndArea}
          {verticalMarkerLineAndArea}
          {getLimitLines()}
          {getChartContent()}
        </ChartComponent>
      </ResponsiveContainer>
      {showLegend && (
        <Box as="ul" display="flex" flexWrap="wrap" listStyleType="none" marginTop="8">
          {chartData.lineNames.map((name: string, index: number) => {
            return (
              <LegendItem
                color={colorMap[name]?.name}
                isNotHovered={!!hoveredLegend && hoveredLegend !== name}
                isSelected={!!selectedItems?.includes(name) || selectedItems?.length === 0}
                // eslint-disable-next-line react/no-array-index-key
                key={`${name}_${index}`}
                name={name}
                title={legendLabels[name]}
                onClick={onLegendItemClick}
                setHoveredLegend={setHoveredLegend}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Chart;
